import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const CTASection = () => {
  return (
    <section 
      data-testid="cta-section"
      className="py-16 md:py-20 cta-gradient"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-sans text-sm font-semibold text-brand-green uppercase tracking-wider">
              Ready to Start?
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-dark mt-4 mb-6">
              Let's Build Your <span className="gradient-text">Sustainable Future</span> Together
            </h2>
            <p className="font-sans text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
              Whether you're an investor seeking ESG integration, a corporate pursuing sustainability 
              leadership, or an SME looking to get started, we're here to help you navigate the journey.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button 
                  data-testid="cta-contact-button"
                  className="btn-gradient text-white font-sans font-medium px-8 py-6 text-base"
                >
                  Schedule a Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  variant="outline"
                  data-testid="cta-services-button"
                  className="font-sans font-medium px-8 py-6 text-base border-2 border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white"
                >
                  Explore Services
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Right Content - Contact Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            {/* Who We Work With */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="font-serif text-xl text-brand-dark mb-6">We Work With</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-brand-surface rounded-xl">
                  <div className="w-2 h-2 rounded-full bg-brand-green mt-2"></div>
                  <div>
                    <p className="font-sans font-medium text-brand-dark">Investors, VCs & Fund Managers</p>
                    <p className="font-sans text-sm text-gray-600">ESG Due Diligence & Portfolio Integration</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-surface rounded-xl">
                  <div className="w-2 h-2 rounded-full bg-brand-brown mt-2"></div>
                  <div>
                    <p className="font-sans font-medium text-brand-dark">Corporates, EPCs & Industrials</p>
                    <p className="font-sans text-sm text-gray-600">Sustainability Strategy, Carbon Readiness & Compliance</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-brand-surface rounded-xl">
                  <div className="w-2 h-2 rounded-full bg-brand-green mt-2"></div>
                  <div>
                    <p className="font-sans font-medium text-brand-dark">SMEs & Project Developers</p>
                    <p className="font-sans text-sm text-gray-600">Carbon Projects, MRV & Market Readiness</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact */}
            <div className="bg-brand-dark rounded-2xl p-8 text-white">
              <h3 className="font-serif text-xl mb-6">Quick Contact</h3>
              <div className="space-y-4">
                <a 
                  href="mailto:info@mapleintegratedsolutions.com"
                  className="flex items-center gap-4 hover:text-brand-green transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span className="font-sans text-sm">info@mapleintegratedsolutions.com</span>
                </a>
                <a 
                  href="tel:+917738481009"
                  className="flex items-center gap-4 hover:text-brand-green transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span className="font-sans text-sm">+91 77384 81009</span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
